import { useState, useEffect, useMemo } from 'react'
import {
  ArrowLeft, TrendingUp, Download, Loader2, Calendar,
  Building, Filter, FileBarChart, AlertTriangle, Layers
} from 'lucide-react'
import { supabase } from '../../lib/supabase'
import { fetchAllIn, fetchAllRows } from '../../lib/fetchAll'

interface Props {
  onBack: () => void
}

interface Center {
  id: string
  name: string
}

interface SessionRow {
  id: string
  periode: string
  semaine: string
  start_date: string
  center_id: string
  capacity: number
}

interface RegistrationRow {
  id: string
  activity_id: string
  amount_paid: number
  price_type: string
  payment_status: string
  cancellation_status: string
  created_at: string
}

interface InvoiceRow {
  id: string
  amount: number
  status: string
  registration_ids: string[]
}

const PRICE_TYPE_LABELS: Record<string, string> = {
  normal: 'Tarif normal',
  reduced: 'Tarif reduit',
  local: 'Tarif local',
  local_reduced: 'Tarif local reduit',
  late: 'Apres la date limite',
  late_reduced: 'Reduit, apres la date limite',
}

export function FinancialReport({ onBack }: Props) {
  const [centers, setCenters] = useState<Center[]>([])
  const [availableYears, setAvailableYears] = useState<string[]>([])
  const [availablePeriods, setAvailablePeriods] = useState<string[]>([])
  const [availableWeeks, setAvailableWeeks] = useState<string[]>([])

  const [selectedYear, setSelectedYear] = useState('')
  const [selectedPeriod, setSelectedPeriod] = useState('')
  const [selectedCenter, setSelectedCenter] = useState('')
  const [selectedWeek, setSelectedWeek] = useState('')

  const [loading, setLoading] = useState(false)
  const [generated, setGenerated] = useState(false)

  const [sessions, setSessions] = useState<SessionRow[]>([])
  const [registrations, setRegistrations] = useState<RegistrationRow[]>([])
  const [invoices, setInvoices] = useState<InvoiceRow[]>([])

  useEffect(() => {
    const fetchBase = async () => {
      const [centersRes, sessionsRes] = await Promise.all([
        supabase.from('centers').select('id, name').eq('active', true).order('name'),
        supabase.from('sessions').select('start_date, periode'),
      ])
      setCenters(centersRes.data || [])
      if (sessionsRes.data) {
        const years = [...new Set(sessionsRes.data.map(s => new Date(s.start_date).getFullYear().toString()))].sort().reverse()
        setAvailableYears(years)
      }
    }
    fetchBase()
  }, [])

  useEffect(() => {
    setSelectedPeriod('')
    setSelectedWeek('')
    setAvailableWeeks([])
    if (!selectedYear) { setAvailablePeriods([]); return }

    const fetchPeriods = async () => {
      let query = supabase.from('sessions').select('periode').gte('start_date', `${selectedYear}-01-01`).lte('start_date', `${selectedYear}-12-31`)
      if (selectedCenter) query = query.eq('center_id', selectedCenter)
      const { data } = await query
      if (data) setAvailablePeriods([...new Set(data.map(s => s.periode))].sort())
    }
    fetchPeriods()
  }, [selectedYear, selectedCenter])

  useEffect(() => {
    setSelectedWeek('')
    if (!selectedYear || !selectedPeriod) { setAvailableWeeks([]); return }

    const fetchWeeks = async () => {
      let query = supabase.from('sessions').select('semaine').eq('periode', selectedPeriod).gte('start_date', `${selectedYear}-01-01`).lte('start_date', `${selectedYear}-12-31`)
      if (selectedCenter) query = query.eq('center_id', selectedCenter)
      const { data } = await query
      if (data) setAvailableWeeks([...new Set(data.map(s => s.semaine))].sort())
    }
    fetchWeeks()
  }, [selectedYear, selectedPeriod, selectedCenter])

  const handleGenerate = async () => {
    setLoading(true)
    setGenerated(false)

    try {
      let sessQuery = supabase.from('sessions').select('id, periode, semaine, start_date, center_id, capacity')
      if (selectedYear) {
        sessQuery = sessQuery.gte('start_date', `${selectedYear}-01-01`).lte('start_date', `${selectedYear}-12-31`)
      }
      if (selectedCenter) sessQuery = sessQuery.eq('center_id', selectedCenter)
      if (selectedPeriod) sessQuery = sessQuery.eq('periode', selectedPeriod)
      if (selectedWeek) sessQuery = sessQuery.eq('semaine', selectedWeek)

      const { data: sessData } = await sessQuery
      setSessions(sessData || [])

      if (!sessData || sessData.length === 0) {
        setRegistrations([])
        setInvoices([])
        setGenerated(true)
        setLoading(false)
        return
      }

      const sessionIds = sessData.map(s => s.id)
      const allRegs = await fetchAllIn<RegistrationRow>(
        'registrations',
        'id, activity_id, amount_paid, price_type, payment_status, cancellation_status, created_at',
        'activity_id',
        sessionIds,
      )
      setRegistrations(allRegs)

      const regIds = new Set(allRegs.map(r => r.id))
      const allInvoices = await fetchAllRows<InvoiceRow>(
        'invoices',
        'id, amount, status, registration_ids',
      )

      const filtered = allInvoices.filter(inv =>
        inv.registration_ids && inv.registration_ids.some((rid: string) => regIds.has(rid))
      )
      setInvoices(filtered)

      setGenerated(true)
    } catch (err) {
      console.error('Error generating financial report:', err)
    } finally {
      setLoading(false)
    }
  }

  const centersMap = useMemo(() => {
    const map = new Map<string, string>()
    for (const c of centers) map.set(c.id, c.name)
    return map
  }, [centers])

  const sessionCenterMap = useMemo(() => {
    const map = new Map<string, string>()
    for (const s of sessions) map.set(s.id, s.center_id)
    return map
  }, [sessions])

  const sessionPeriodeMap = useMemo(() => {
    const map = new Map<string, string>()
    for (const s of sessions) map.set(s.id, s.periode)
    return map
  }, [sessions])

  const sessionWeekMap = useMemo(() => {
    const map = new Map<string, string>()
    for (const s of sessions) map.set(s.id, s.semaine)
    return map
  }, [sessions])

  const activeRegs = useMemo(() =>
    registrations.filter(r => r.cancellation_status === 'none' || !r.cancellation_status || r.cancellation_status === ''),
    [registrations]
  )

  const cancelledRegs = useMemo(() =>
    registrations.filter(r => r.cancellation_status && r.cancellation_status !== 'none' && r.cancellation_status !== ''),
    [registrations]
  )

  const summary = useMemo(() => {
    const totalBilled = activeRegs.reduce((s, r) => s + (Number(r.amount_paid) || 0), 0)
    const totalPaid = activeRegs.filter(r => r.payment_status === 'paid').reduce((s, r) => s + (Number(r.amount_paid) || 0), 0)
    const totalPending = activeRegs.filter(r => r.payment_status === 'pending').reduce((s, r) => s + (Number(r.amount_paid) || 0), 0)
    const recoveryRate = totalBilled > 0 ? (totalPaid / totalBilled * 100) : 0

    const invPaid = invoices.filter(i => i.status === 'paid').length
    const invPending = invoices.filter(i => i.status === 'pending').length
    const invCancelled = invoices.filter(i => i.status === 'cancelled').length

    const cancelledFullRefund = cancelledRegs.filter(r => r.cancellation_status === 'cancelled_full_refund')
    const cancelledPartialRefund = cancelledRegs.filter(r => r.cancellation_status === 'cancelled_partial_refund')
    const cancelledNoRefund = cancelledRegs.filter(r => r.cancellation_status === 'cancelled_no_refund')
    const cancelledRequested = cancelledRegs.filter(r => r.cancellation_status === 'requested')

    return {
      totalBilled, totalPaid, totalPending, recoveryRate,
      totalInvoices: invoices.length, invPaid, invPending, invCancelled,
      totalCancelled: cancelledRegs.length,
      cancelledAmount: cancelledRegs.reduce((s, r) => s + (Number(r.amount_paid) || 0), 0),
      cancelledFullRefund: cancelledFullRefund.length,
      cancelledPartialRefund: cancelledPartialRefund.length,
      cancelledNoRefund: cancelledNoRefund.length,
      cancelledRequested: cancelledRequested.length,
      totalRegistrations: activeRegs.length,
    }
  }, [activeRegs, cancelledRegs, invoices])

  const byPriceType = useMemo(() => {
    const map = new Map<string, { count: number; total: number }>()
    for (const r of activeRegs) {
      const pt = (r.price_type || '').trim()
      const entry = map.get(pt) || { count: 0, total: 0 }
      entry.count++
      entry.total += Number(r.amount_paid) || 0
      map.set(pt, entry)
    }
    const totalAll = activeRegs.reduce((s, r) => s + (Number(r.amount_paid) || 0), 0)
    return [...map.entries()]
      .map(([type, d]) => ({
        type,
        label: PRICE_TYPE_LABELS[type] || type,
        count: d.count,
        total: d.total,
        avg: d.count > 0 ? d.total / d.count : 0,
        pct: totalAll > 0 ? (d.total / totalAll * 100) : 0,
      }))
      .sort((a, b) => b.total - a.total)
  }, [activeRegs])

  const byCenter = useMemo(() => {
    if (selectedCenter) return []
    const map = new Map<string, { count: number; billed: number; paid: number }>()
    for (const r of activeRegs) {
      const centerId = sessionCenterMap.get(r.activity_id) || 'unknown'
      const entry = map.get(centerId) || { count: 0, billed: 0, paid: 0 }
      entry.count++
      entry.billed += Number(r.amount_paid) || 0
      if (r.payment_status === 'paid') entry.paid += Number(r.amount_paid) || 0
      map.set(centerId, entry)
    }
    return [...map.entries()]
      .map(([cid, d]) => ({
        centerId: cid,
        name: centersMap.get(cid) || 'Inconnu',
        count: d.count,
        billed: d.billed,
        paid: d.paid,
        rate: d.billed > 0 ? (d.paid / d.billed * 100) : 0,
      }))
      .sort((a, b) => b.billed - a.billed)
  }, [activeRegs, selectedCenter, sessionCenterMap, centersMap])

  const byPeriode = useMemo(() => {
    if (selectedPeriod) return []
    const map = new Map<string, { count: number; billed: number; paid: number }>()
    for (const r of activeRegs) {
      const per = sessionPeriodeMap.get(r.activity_id) || 'Inconnu'
      const entry = map.get(per) || { count: 0, billed: 0, paid: 0 }
      entry.count++
      entry.billed += Number(r.amount_paid) || 0
      if (r.payment_status === 'paid') entry.paid += Number(r.amount_paid) || 0
      map.set(per, entry)
    }
    return [...map.entries()]
      .map(([per, d]) => ({ periode: per, ...d, rate: d.billed > 0 ? (d.paid / d.billed * 100) : 0 }))
      .sort((a, b) => b.billed - a.billed)
  }, [activeRegs, selectedPeriod, sessionPeriodeMap])

  const byWeek = useMemo(() => {
    if (!selectedPeriod || selectedWeek) return []
    const map = new Map<string, { count: number; billed: number; capacity: number }>()

    const weekCapacity = new Map<string, number>()
    for (const s of sessions) {
      const cur = weekCapacity.get(s.semaine) || 0
      weekCapacity.set(s.semaine, cur + (s.capacity || 0))
    }

    for (const r of activeRegs) {
      const sem = sessionWeekMap.get(r.activity_id) || 'Inconnu'
      const entry = map.get(sem) || { count: 0, billed: 0, capacity: weekCapacity.get(sem) || 0 }
      entry.count++
      entry.billed += Number(r.amount_paid) || 0
      map.set(sem, entry)
    }
    return [...map.entries()]
      .map(([sem, d]) => ({
        semaine: sem,
        count: d.count,
        billed: d.billed,
        capacity: d.capacity,
        fillRate: d.capacity > 0 ? (d.count / d.capacity * 100) : 0,
      }))
      .sort((a, b) => a.semaine.localeCompare(b.semaine))
  }, [activeRegs, selectedPeriod, selectedWeek, sessionWeekMap, sessions])

  const fmt = (n: number) => n.toLocaleString('fr-BE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' EUR'
  const fmtPct = (n: number) => n.toFixed(1) + ' %'

  const exportCSV = () => {
    const lines: string[] = []
    lines.push('Rapport financier')
    lines.push(`Filtres: ${selectedYear || 'Toutes annees'} | ${selectedPeriod || 'Toutes periodes'} | ${selectedCenter ? centersMap.get(selectedCenter) : 'Tous centres'} | ${selectedWeek || 'Toutes semaines'}`)
    lines.push('')
    lines.push('Synthese')
    lines.push(`Inscriptions actives;${summary.totalRegistrations}`)
    lines.push(`Montant facture;${summary.totalBilled}`)
    lines.push(`Montant encaisse;${summary.totalPaid}`)
    lines.push(`Montant en attente;${summary.totalPending}`)
    lines.push(`Taux de recouvrement;${summary.recoveryRate.toFixed(1)}%`)
    lines.push(`Factures totales;${summary.totalInvoices}`)
    lines.push(`Factures payees;${summary.invPaid}`)
    lines.push(`Factures en attente;${summary.invPending}`)
    lines.push(`Factures annulees;${summary.invCancelled}`)
    lines.push(`Annulations totales;${summary.totalCancelled}`)
    lines.push(`Montant annulations;${summary.cancelledAmount}`)
    lines.push('')
    lines.push('Repartition par tarif')
    lines.push('Type;Nombre;Montant total;Montant moyen;Pourcentage')
    for (const row of byPriceType) {
      lines.push(`${row.label};${row.count};${row.total};${row.avg.toFixed(2)};${row.pct.toFixed(1)}%`)
    }
    if (byCenter.length > 0) {
      lines.push('')
      lines.push('Repartition par centre')
      lines.push('Centre;Inscriptions;Facture;Encaisse;Taux recouvrement')
      for (const row of byCenter) {
        lines.push(`${row.name};${row.count};${row.billed};${row.paid};${row.rate.toFixed(1)}%`)
      }
    }
    if (byPeriode.length > 0) {
      lines.push('')
      lines.push('Repartition par periode')
      lines.push('Periode;Inscriptions;Facture;Encaisse;Taux recouvrement')
      for (const row of byPeriode) {
        lines.push(`${row.periode};${row.count};${row.billed};${row.paid};${row.rate.toFixed(1)}%`)
      }
    }
    if (byWeek.length > 0) {
      lines.push('')
      lines.push('Repartition par semaine')
      lines.push('Semaine;Inscriptions;Facture;Capacite;Taux remplissage')
      for (const row of byWeek) {
        lines.push(`${row.semaine};${row.count};${row.billed};${row.capacity};${row.fillRate.toFixed(1)}%`)
      }
    }
    const csv = lines.join('\n')
    const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `Rapport_financier_${selectedYear || 'tout'}_${selectedPeriod || 'tout'}.csv`
    a.click()
    URL.revokeObjectURL(url)
  }

  const filterLabel = [
    selectedYear || 'Toutes annees',
    selectedPeriod || 'Toutes periodes',
    selectedCenter ? centersMap.get(selectedCenter) : 'Tous centres',
    selectedWeek || 'Toutes semaines',
  ].join(' - ')

  return (
    <div className="space-y-6 print:space-y-4">
      <div className="flex items-center gap-4 print:hidden">
        <button onClick={onBack} className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-900 transition-colors">
          <ArrowLeft className="h-4 w-4" /> Retour
        </button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Rapport financier</h1>
          <p className="text-gray-600">Analyse des inscriptions, paiements et factures</p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 print:hidden">
        <div className="grid grid-cols-1 sm:grid-cols-5 gap-4 items-end">
          <div>
            <label className="flex items-center gap-1.5 text-sm font-medium text-gray-700 mb-1">
              <Calendar className="h-4 w-4" /> Annee
            </label>
            <select value={selectedYear} onChange={e => setSelectedYear(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500">
              <option value="">Toutes</option>
              {availableYears.map(y => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>
          <div>
            <label className="flex items-center gap-1.5 text-sm font-medium text-gray-700 mb-1">
              <Building className="h-4 w-4" /> Centre
            </label>
            <select value={selectedCenter} onChange={e => setSelectedCenter(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500">
              <option value="">Tous</option>
              {centers.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <div>
            <label className="flex items-center gap-1.5 text-sm font-medium text-gray-700 mb-1">
              <Filter className="h-4 w-4" /> Periode
            </label>
            <select value={selectedPeriod} onChange={e => setSelectedPeriod(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500">
              <option value="">Toutes</option>
              {availablePeriods.map(p => <option key={p} value={p}>{p}</option>)}
            </select>
          </div>
          <div>
            <label className="flex items-center gap-1.5 text-sm font-medium text-gray-700 mb-1">
              <Layers className="h-4 w-4" /> Semaine
            </label>
            <select value={selectedWeek} onChange={e => setSelectedWeek(e.target.value)}
              disabled={!selectedPeriod}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100">
              <option value="">Toutes</option>
              {availableWeeks.map(w => <option key={w} value={w}>{w}</option>)}
            </select>
          </div>
          <button onClick={handleGenerate} disabled={loading}
            className={`flex items-center justify-center gap-2 px-6 py-2 rounded-lg text-sm font-medium transition-colors ${
              !loading ? 'bg-blue-600 text-white hover:bg-blue-700' : 'bg-gray-200 text-gray-400 cursor-not-allowed'
            }`}>
            {loading ? <><Loader2 className="h-4 w-4 animate-spin" /> Chargement...</> : <><FileBarChart className="h-4 w-4" /> Generer</>}
          </button>
        </div>
      </div>

      {/* Print header */}
      <div className="hidden print:block text-center mb-4">
        <h1 className="text-xl font-bold">Rapport financier</h1>
        <p className="text-sm text-gray-600">{filterLabel}</p>
      </div>

      {generated && (
        <>
          {sessions.length === 0 ? (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
              <AlertTriangle className="h-8 w-8 text-amber-500 mx-auto mb-3" />
              <p className="text-gray-600">Aucune session trouvee pour cette selection.</p>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Export buttons */}
              <div className="flex justify-end gap-2 print:hidden">
                <button onClick={exportCSV}
                  className="flex items-center gap-1.5 px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors">
                  <Download className="h-4 w-4" /> CSV
                </button>
                <button onClick={() => window.print()}
                  className="flex items-center gap-1.5 px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors">
                  <Download className="h-4 w-4" /> Imprimer / PDF
                </button>
              </div>

              {/* Summary cards */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <SummaryCard label="Montant facture" value={fmt(summary.totalBilled)} sub={`${summary.totalRegistrations} inscriptions`} color="blue" />
                <SummaryCard label="Montant encaisse" value={fmt(summary.totalPaid)} sub={`${summary.invPaid} factures payees`} color="green" />
                <SummaryCard label="Montant en attente" value={fmt(summary.totalPending)} sub={`${summary.invPending} factures en attente`} color="amber" />
                <SummaryCard label="Taux de recouvrement" value={fmtPct(summary.recoveryRate)} sub={`${summary.totalInvoices} factures totales`} color="teal" />
              </div>

              {/* Cancellations summary */}
              {summary.totalCancelled > 0 && (
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5 print:shadow-none print:border">
                  <h3 className="text-sm font-semibold text-gray-900 mb-3">Annulations</h3>
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-sm">
                    <div>
                      <p className="text-gray-500">Total</p>
                      <p className="text-lg font-bold text-gray-900">{summary.totalCancelled}</p>
                      <p className="text-xs text-gray-400">{fmt(summary.cancelledAmount)}</p>
                    </div>
                    <div>
                      <p className="text-gray-500">Remboursement total</p>
                      <p className="text-lg font-bold text-red-600">{summary.cancelledFullRefund}</p>
                    </div>
                    <div>
                      <p className="text-gray-500">Remboursement partiel</p>
                      <p className="text-lg font-bold text-orange-600">{summary.cancelledPartialRefund}</p>
                    </div>
                    <div>
                      <p className="text-gray-500">Sans remboursement</p>
                      <p className="text-lg font-bold text-gray-600">{summary.cancelledNoRefund}</p>
                    </div>
                    <div>
                      <p className="text-gray-500">En attente</p>
                      <p className="text-lg font-bold text-amber-600">{summary.cancelledRequested}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* By price type */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden print:shadow-none print:border print:break-inside-avoid">
                <div className="bg-gray-50 border-b px-4 py-3">
                  <h3 className="text-sm font-semibold text-gray-900">Repartition par type de tarif</h3>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-gray-200">
                        <th className="text-left px-4 py-2 font-medium text-gray-600">Type de tarif</th>
                        <th className="text-center px-4 py-2 font-medium text-gray-600">Inscriptions</th>
                        <th className="text-right px-4 py-2 font-medium text-gray-600">Montant total</th>
                        <th className="text-right px-4 py-2 font-medium text-gray-600">Montant moyen</th>
                        <th className="text-right px-4 py-2 font-medium text-gray-600">% du total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {byPriceType.map(row => (
                        <tr key={row.type} className="border-b border-gray-100 hover:bg-gray-50">
                          <td className="px-4 py-2 font-medium text-gray-900">{row.label}</td>
                          <td className="text-center px-4 py-2 text-gray-700 tabular-nums">{row.count}</td>
                          <td className="text-right px-4 py-2 text-gray-900 tabular-nums">{fmt(row.total)}</td>
                          <td className="text-right px-4 py-2 text-gray-700 tabular-nums">{fmt(row.avg)}</td>
                          <td className="text-right px-4 py-2">
                            <div className="flex items-center justify-end gap-2">
                              <div className="w-16 h-2 bg-gray-100 rounded-full overflow-hidden">
                                <div className="h-full bg-blue-500 rounded-full" style={{ width: `${Math.min(row.pct, 100)}%` }} />
                              </div>
                              <span className="text-gray-700 tabular-nums w-12 text-right">{fmtPct(row.pct)}</span>
                            </div>
                          </td>
                        </tr>
                      ))}
                      <tr className="border-t-2 border-gray-300 bg-gray-50 font-bold">
                        <td className="px-4 py-2 text-gray-900">Total</td>
                        <td className="text-center px-4 py-2 text-gray-900 tabular-nums">{summary.totalRegistrations}</td>
                        <td className="text-right px-4 py-2 text-gray-900 tabular-nums">{fmt(summary.totalBilled)}</td>
                        <td className="text-right px-4 py-2"></td>
                        <td className="text-right px-4 py-2 text-gray-900 tabular-nums">100 %</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              {/* By center */}
              {byCenter.length > 0 && (
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden print:shadow-none print:border print:break-inside-avoid">
                  <div className="bg-gray-50 border-b px-4 py-3">
                    <h3 className="text-sm font-semibold text-gray-900">Repartition par centre</h3>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-gray-200">
                          <th className="text-left px-4 py-2 font-medium text-gray-600">Centre</th>
                          <th className="text-center px-4 py-2 font-medium text-gray-600">Inscriptions</th>
                          <th className="text-right px-4 py-2 font-medium text-gray-600">Montant facture</th>
                          <th className="text-right px-4 py-2 font-medium text-gray-600">Montant encaisse</th>
                          <th className="text-right px-4 py-2 font-medium text-gray-600">Taux recouvrement</th>
                        </tr>
                      </thead>
                      <tbody>
                        {byCenter.map(row => (
                          <tr key={row.centerId} className="border-b border-gray-100 hover:bg-gray-50">
                            <td className="px-4 py-2 font-medium text-gray-900">{row.name}</td>
                            <td className="text-center px-4 py-2 text-gray-700 tabular-nums">{row.count}</td>
                            <td className="text-right px-4 py-2 text-gray-900 tabular-nums">{fmt(row.billed)}</td>
                            <td className="text-right px-4 py-2 text-gray-700 tabular-nums">{fmt(row.paid)}</td>
                            <td className="text-right px-4 py-2">
                              <RateBar value={row.rate} />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* By period */}
              {byPeriode.length > 0 && (
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden print:shadow-none print:border print:break-inside-avoid">
                  <div className="bg-gray-50 border-b px-4 py-3">
                    <h3 className="text-sm font-semibold text-gray-900">Repartition par periode</h3>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-gray-200">
                          <th className="text-left px-4 py-2 font-medium text-gray-600">Periode</th>
                          <th className="text-center px-4 py-2 font-medium text-gray-600">Inscriptions</th>
                          <th className="text-right px-4 py-2 font-medium text-gray-600">Montant facture</th>
                          <th className="text-right px-4 py-2 font-medium text-gray-600">Montant encaisse</th>
                          <th className="text-right px-4 py-2 font-medium text-gray-600">Taux recouvrement</th>
                        </tr>
                      </thead>
                      <tbody>
                        {byPeriode.map(row => (
                          <tr key={row.periode} className="border-b border-gray-100 hover:bg-gray-50">
                            <td className="px-4 py-2 font-medium text-gray-900">{row.periode}</td>
                            <td className="text-center px-4 py-2 text-gray-700 tabular-nums">{row.count}</td>
                            <td className="text-right px-4 py-2 text-gray-900 tabular-nums">{fmt(row.billed)}</td>
                            <td className="text-right px-4 py-2 text-gray-700 tabular-nums">{fmt(row.paid)}</td>
                            <td className="text-right px-4 py-2">
                              <RateBar value={row.rate} />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* By week */}
              {byWeek.length > 0 && (
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden print:shadow-none print:border print:break-inside-avoid">
                  <div className="bg-gray-50 border-b px-4 py-3">
                    <h3 className="text-sm font-semibold text-gray-900">Detail par semaine</h3>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-gray-200">
                          <th className="text-left px-4 py-2 font-medium text-gray-600">Semaine</th>
                          <th className="text-center px-4 py-2 font-medium text-gray-600">Inscriptions</th>
                          <th className="text-right px-4 py-2 font-medium text-gray-600">Montant facture</th>
                          <th className="text-center px-4 py-2 font-medium text-gray-600">Capacite</th>
                          <th className="text-right px-4 py-2 font-medium text-gray-600">Taux remplissage</th>
                        </tr>
                      </thead>
                      <tbody>
                        {byWeek.map(row => (
                          <tr key={row.semaine} className="border-b border-gray-100 hover:bg-gray-50">
                            <td className="px-4 py-2 font-medium text-gray-900">{row.semaine}</td>
                            <td className="text-center px-4 py-2 text-gray-700 tabular-nums">{row.count}</td>
                            <td className="text-right px-4 py-2 text-gray-900 tabular-nums">{fmt(row.billed)}</td>
                            <td className="text-center px-4 py-2 text-gray-700 tabular-nums">{row.capacity}</td>
                            <td className="text-right px-4 py-2">
                              <RateBar value={row.fillRate} color={row.fillRate > 90 ? 'red' : row.fillRate > 70 ? 'amber' : 'green'} />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  )
}

function SummaryCard({ label, value, sub, color }: { label: string; value: string; sub: string; color: string }) {
  const colors: Record<string, { bg: string; text: string; subText: string }> = {
    blue: { bg: 'bg-blue-50 border-blue-200', text: 'text-blue-800', subText: 'text-blue-600' },
    green: { bg: 'bg-emerald-50 border-emerald-200', text: 'text-emerald-800', subText: 'text-emerald-600' },
    amber: { bg: 'bg-amber-50 border-amber-200', text: 'text-amber-800', subText: 'text-amber-600' },
    teal: { bg: 'bg-teal-50 border-teal-200', text: 'text-teal-800', subText: 'text-teal-600' },
  }
  const c = colors[color] || colors.blue
  return (
    <div className={`rounded-xl border p-4 ${c.bg}`}>
      <p className={`text-xs font-medium ${c.subText} mb-1`}>{label}</p>
      <p className={`text-xl font-bold ${c.text} tabular-nums`}>{value}</p>
      <p className={`text-xs ${c.subText} mt-1`}>{sub}</p>
    </div>
  )
}

function RateBar({ value, color = 'blue' }: { value: number; color?: string }) {
  const barColor = color === 'red' ? 'bg-red-500' : color === 'amber' ? 'bg-amber-500' : color === 'green' ? 'bg-emerald-500' : 'bg-blue-500'
  return (
    <div className="flex items-center justify-end gap-2">
      <div className="w-16 h-2 bg-gray-100 rounded-full overflow-hidden">
        <div className={`h-full ${barColor} rounded-full`} style={{ width: `${Math.min(value, 100)}%` }} />
      </div>
      <span className="text-gray-700 tabular-nums w-12 text-right">{value.toFixed(1)} %</span>
    </div>
  )
}
